import { Card } from "@/components/ui/card";
import { VehicleInformation } from "./VehicleInformation";
import { Info } from "lucide-react";
import { ColoredOutlineButton, OUTLINE_ICON_BUTTON_ICON_CLASS, OUTLINE_TEXT_BUTTON_ICON_CLASS } from "@/components/ui/colored-outline-button";
import { useState } from "react";
import { DescriptionPopup } from "../auctions/DescriptionDialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels } from "@/i18n";

import { ListingDetails } from "@/types/listing";

interface CarBasicInfoProps {
  listing: ListingDetails;
  equipment?: string[];
  isGuestMode?: boolean;
  equipmentTotalCount?: number;
  onGuestLoginRequired?: () => void;
}

const CarBasicInfo = ({
  listing,
  equipment,
  isGuestMode,
  equipmentTotalCount,
  onGuestLoginRequired,
}: CarBasicInfoProps) => {
  const [showDescriptionPopup, setShowDescriptionPopup] = useState(false);
  const isMobile = useIsMobile();
  const { language } = useLanguage();
  const labels = carDetailsLabels(language);
  const descriptionText = (listing.description ?? "").trim();
  const showDescriptionButton = descriptionText.length >= 3;

  const handleDescriptionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDescriptionPopup(true);
  };

  const descriptionButton = showDescriptionButton ? (
    isMobile ? (
      <ColoredOutlineButton
        type="button"
        variant="blue"
        tintedBackground
        onClick={handleDescriptionClick}
        size="icon"
        innerClassName="flex-shrink-0"
      >
        <Info className={OUTLINE_ICON_BUTTON_ICON_CLASS} strokeWidth={2.35} />
      </ColoredOutlineButton>
    ) : (
      <ColoredOutlineButton
        type="button"
        variant="blue"
        tintedBackground
        onClick={handleDescriptionClick}
        size="sm"
        innerClassName="px-4 py-2 flex-shrink-0 text-sm"
      >
        <Info className={OUTLINE_TEXT_BUTTON_ICON_CLASS} strokeWidth={2.35} />
        <span>{labels.description}</span>
      </ColoredOutlineButton>
    )
  ) : null;

  return (
    <>
      <Card className={"glass-card animate-fade-in"}>
        <div className={`p-4 md:p-6 space-y-3`}>
          <div className={"mt-2 px-0"}>
            <h1 className={`text-2xl md:text-3xl font-bold`}>
              {listing.title ||
                `${listing.vehicleDetails.make} ${listing.vehicleDetails.model} ${listing.vehicleDetails.year}`}
            </h1>
            {listing.title ? (
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <p
                  className={`text-lg md:text-xl text-gray-600 dark:text-gray-300`}
                >
                  {listing.vehicleDetails.make} {listing.vehicleDetails.model}{" "}
                  {listing.vehicleDetails.year}
                </p>
                {descriptionButton}
              </div>
            ) : (
              descriptionButton ? (
                <div className="flex items-center gap-2 mt-2">
                  {descriptionButton}
                </div>
              ) : null
            )}
          </div>
          <VehicleInformation
            vehicleDetails={listing.vehicleDetails}
            vehicleTest={listing.vehicleTest}
            equipment={equipment}
            showAdditionalInfo={true}
            isGuestMode={isGuestMode}
            equipmentTotalCount={equipmentTotalCount}
            onGuestLoginRequired={onGuestLoginRequired}
          />
        </div>
      </Card>
      {showDescriptionPopup && (
        <DescriptionPopup
          description={listing.description || labels.noDescription}
          title={`${listing.vehicleDetails.make} ${listing.vehicleDetails.model}`}
          onClose={() => setShowDescriptionPopup(false)}
        />
      )}
    </>
  );
};

export default CarBasicInfo;
